import {
	IUserEntityAttributes, UserEntity, IStudyPlanEntityAttributes, ICurriculumSupportEntityAttributes,
	IInstitutionAdminEntityAttributes, IStudentAdvisorEntityAttributes,
	IAdminEntityAttributes,
} from 'Models/Entities';
import gql from 'graphql-tag';
import { store } from 'Models/Store';
import { Conditions, GraphQlQueryHookParams, GraphQlQueryRequest } from 'Util/QueryHelpers';
import { AbortSignals } from 'Hooks/useAbortSignals';
import { getAttributes, getFetchSingleQuery, getModelName } from 'Util/EntityUtils';
import { UserStatusFields } from 'Util/UserUtils';
import { ModelType } from 'Util/TypeHelpers';
import { ApolloQueryResult } from '@apollo/client';
import { lowerCaseFirst } from 'Util/StringUtils';

export const GET_ALL_STUDENT_ENTITIES = gql`
	query studentEntitys(
		$conditions: [[WhereExpressionGraph]],
		$has: [[HasConditionType]],
		$skip: Int,
		$take: Int,
		$orderBy: [OrderByGraph],
		$ids: [ID],
		$id: ID
	) {
		userEntitys(
			conditions: $conditions
			has: $has
			skip: $skip
			take: $take
			orderBy: $orderBy
			ids: $ids
			id: $id
	) {
			id
			studentId
			firstName
			lastName
			created
			modified
			email
			lastAccessed
			lockoutEnd
			emailConfirmed
			studyPlans {
				id
				name
				active
				programVersion {
					id
					program {
						id
						code
						displayName
					}
				}
			}
		}
		countUserEntitys(
			conditions: $conditions
			has: $has
			ids: $ids
			id: $id
	) {
			number
			__typename
		}
	}`;

export const GET_ALL_ENTITIES_OF_STAFF_TYPE = (staffModelType: ModelType, includeNames: boolean) => {
	const modelName = lowerCaseFirst(getModelName(staffModelType));

	return gql`
		query staffEntitys(
			$conditions: [[WhereExpressionGraph]],
			$has: [[HasConditionType]],
			$skip: Int,
			$take: Int,
			$orderBy: [OrderByGraph],
			$ids: [ID],
			$id: ID
		) {
			${modelName}s(
				conditions: $conditions
				has: $has
				skip: $skip
				take: $take
				orderBy: $orderBy
				ids: $ids
				id: $id
		) {
				id
				${includeNames ? 'firstName, lastName,' : ''}
				created
				modified
				email
				lastAccessed
				lockoutEnd
				emailConfirmed
			}
	}`;
};

export const GET_USER_WITH_ACTIVE_PLAN = (userModelType: ModelType) => gql`
	query userWithActivePlanQuery(
		$id: ID,
		$conditions: [[WhereExpressionGraph]],
	) {
		userEntity(id: $id) {
			${getAttributes(userModelType).join('\n')}
			lockoutEnd
			emailConfirmed
			lastAccessed
			studyPlans {
				id
				active
				programVersion {
					id
					versionDisplayName
					program {
						id
						title
						displayName
					}
				}
				__typename
			}
			__typename
		}
		studyPlanEntity(conditions: $conditions) {
			id
			active
			studentId
			programVersion {
				id
				versionDisplayName
				program {
					id
					code
					displayName
				}
			}
			__typename
		}
	}
`;

type AllStudentsResponseData = {
	userEntitys: IUserEntityAttributes[],
	countUserEntitys: { number: number }
};

export const getAllStudents: GraphQlQueryRequest<AllStudentsResponseData> = (
	abortSignals,
	conditions,
	has,
	orderBy,
	skip,
	take,
) => (
	store.apolloClient
		.query<AllStudentsResponseData>(
			{
				query: GET_ALL_STUDENT_ENTITIES,
				variables: {
					conditions,
					has,
					orderBy,
					skip,
					take,
				},
				fetchPolicy: 'network-only',
				context: {
					fetchOptions: {
						signal: abortSignals.abortSignal,
					},
				},
			},
		)
);

type AllStaffResponseData = {
	institutionAdminEntitys: IInstitutionAdminEntityAttributes[];
	countInstitutionAdminEntitys: { number: number }
} | {
	curriculumSupportEntitys: ICurriculumSupportEntityAttributes[];
	countCurriculumSupportEntitys: { number: number }
} | {
	studentAdvisorEntitys: IStudentAdvisorEntityAttributes[];
	countStudentAdvisorEntitys: { number: number }
}
| {
	adminEntitys: IAdminEntityAttributes[];
	countAdminEntitys: { number: number }
};

type AllStaffUserRequest<R> = (...params: [...GraphQlQueryHookParams, ModelType, boolean])
	=> Promise<ApolloQueryResult<R>>;

export const getAllStaffUserType: AllStaffUserRequest<AllStaffResponseData> = (
	abortSignals,
	conditions,
	has,
	orderBy,
	skip,
	take,
	ids,
	modelType,
	includeNames,
) => (
	store.apolloClient
		.query<AllStaffResponseData>(
			{
				query: GET_ALL_ENTITIES_OF_STAFF_TYPE(modelType, includeNames),
				variables: {
					conditions,
					has,
					orderBy,
					skip,
					take,
					ids,
				},
				fetchPolicy: 'network-only',
				context: {
					fetchOptions: {
						signal: abortSignals.abortSignal,
					},
				},
			},
		)
);

export const getSingleStudent = (abortSignals: AbortSignals, id: string) => (
	store.apolloClient
		.query<{ userEntity: IUserEntityAttributes & UserStatusFields }>(
			{
				query: getFetchSingleQuery(UserEntity, 'lockoutEnd, emailConfirmed,'),
				variables: {
					args: [
						{
							path: 'id',
							comparison: 'equal',
							value: id,
						},
					],
				},
				fetchPolicy: 'network-only',
				context: {
					fetchOptions: {
						signal: abortSignals.abortSignal,
					},
				},
			},
		)
);

export const getSingleStaff = <R, >(
	abortSignals: AbortSignals,
	id: string,
	modelType: ModelType,
) => (
		store.apolloClient
			.query<R & UserStatusFields>(
				{
					query: getFetchSingleQuery(modelType, 'lockoutEnd, emailConfirmed, lastAccessed,'),
					variables: {
						args: [
							{
								path: 'id',
								comparison: 'equal',
								value: id,
							},
						],
					},
					fetchPolicy: 'network-only',
					context: {
						fetchOptions: {
							signal: abortSignals.abortSignal,
						},
					},
				},
			)
	);

export type ExtendedUserResponse = {
	userEntity: IUserEntityAttributes & UserStatusFields
	studyPlanEntity: IStudyPlanEntityAttributes,
};

export const getStudentWithActivePlan = (
	abortSignal: AbortSignal,
	conditions: Conditions,
	id: string,
) => (
	store.apolloClient
		.query<ExtendedUserResponse>(
			{
				query: GET_USER_WITH_ACTIVE_PLAN(UserEntity),
				variables: {
					conditions,
					id,
				},
				fetchPolicy: 'network-only',
				context: {
					fetchOptions: {
						signal: abortSignal,
					},
				},
			},
		)
);
